import type {
	ClientPortfolioCryptocurrencyInstructionResponseDTO,
	ClientPortfolioDetailBankInstructionResponseDTO,
	ClientPortfolioDetailBondResponseDTO,
	ClientPortfolioDetailCommodityResponseDTO,
	ClientPortfolioDetailInvestmentResponseDTO,
	ClientPortfolioDetailToolsResponseDTO,
} from "web_myplann/Infrastructure/Api/Api";
import {
	type NormalizedInstruction,
	type NormalizedTool,
	ToolKind,
} from "web_myplann/Portfolio/Helpers/investmentToolTypes";

export const normalizeFundInstruction = (
	raw: ClientPortfolioDetailInvestmentResponseDTO,
): NormalizedInstruction<ClientPortfolioDetailInvestmentResponseDTO> => ({
	id: raw.id,
	startAt: raw.startAt,
	endAt: raw.endAt,
	frequency: raw.frequency,
	investmentType: raw.investmentType ?? null,
	raw,
});

export const normalizeBondInstruction = (
	raw: ClientPortfolioDetailBondResponseDTO,
): NormalizedInstruction<ClientPortfolioDetailBondResponseDTO> => ({
	id: raw.id,
	startAt: raw.issueDate,
	endAt: raw.maturityDate,
	frequency: raw.frequency,
	investmentType: null,
	raw,
});

export const normalizeCommodityInstruction = (
	raw: ClientPortfolioDetailCommodityResponseDTO,
): NormalizedInstruction<ClientPortfolioDetailCommodityResponseDTO> => ({
	id: raw.id,
	startAt: raw.startAt,
	endAt: raw.endAt,
	frequency: raw.frequency,
	investmentType: raw.investmentType ?? null,
	raw,
});

export const normalizeCryptoInstruction = (
	raw: ClientPortfolioCryptocurrencyInstructionResponseDTO,
): NormalizedInstruction<ClientPortfolioCryptocurrencyInstructionResponseDTO> => ({
	id: raw.id,
	startAt: raw.date,
	endAt: raw.endedAt,
	frequency: raw.frequency,
	investmentType: raw.investmentType ?? null,
	raw,
});

export const normalizeBankInstruction = (
	raw: ClientPortfolioDetailBankInstructionResponseDTO,
): NormalizedInstruction<ClientPortfolioDetailBankInstructionResponseDTO> => ({
	id: raw.id,
	startAt: raw.startAt,
	endAt: raw.endAt,
	frequency: raw.frequency,
	investmentType: raw.investmentType,
	raw,
});

export const normalizeTools = (
	tools: ClientPortfolioDetailToolsResponseDTO | null | undefined,
): NormalizedTool[] => {
	if (!tools) {
		return [];
	}

	return [
		...tools.funds.map(
			(raw): NormalizedTool => ({
				kind: ToolKind.Investment,
				id: raw.id,
				identificationType: raw.identificationType,
				identification: raw.identification,
				name: raw.name,
				monthlyPayment: raw.monthlyPayment,
				type: raw.type,
				evaluation: raw.evaluation,
				instructions: raw.instructions.map(normalizeFundInstruction),
				raw,
			}),
		),
		...tools.bonds.map(
			(raw): NormalizedTool => ({
				kind: ToolKind.Bond,
				id: raw.id,
				identificationType: raw.identificationType,
				identification: raw.identification,
				name: raw.name,
				monthlyPayment: raw.monthlyPayment,
				evaluation: raw.evaluation,
				instructions: raw.instructions.map(normalizeBondInstruction),
				raw,
			}),
		),
		...tools.commodities.map(
			(raw): NormalizedTool => ({
				kind: ToolKind.Commodity,
				id: raw.id,
				identificationType: raw.identificationType,
				identification: raw.identification,
				name: raw.name,
				monthlyPayment: raw.monthlyPayment,
				instructions: raw.commodities.map(normalizeCommodityInstruction),
				raw,
			}),
		),
		...tools.cryptocurrencies.map(
			(raw): NormalizedTool => ({
				kind: ToolKind.Crypto,
				id: raw.id,
				identificationType: raw.identificationType,
				identification: raw.identification,
				name: raw.name,
				monthlyPayment: raw.monthlyPayment,
				instructions: raw.cryptocurrencyInstructions.map(
					normalizeCryptoInstruction,
				),
				raw,
			}),
		),
		...tools.bankProducts.map(
			(raw): NormalizedTool => ({
				kind: ToolKind.BankProduct,
				id: raw.id,
				identificationType: raw.identificationType,
				identification: raw.identification,
				name: raw.name,
				monthlyPayment: raw.monthlyPayment,
				instructions: raw.instructions.map(normalizeBankInstruction),
				raw,
			}),
		),
	];
};
